import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

interface PlaceResult {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

interface CountyBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

interface GooglePlacesSearchProps {
  onPlaceSelected?: (place: google.maps.places.PlaceResult) => void;
  restrictToViewBounds?: boolean;
  countyBounds?: CountyBounds;
}

const GooglePlacesSearch: React.FC<GooglePlacesSearchProps> = ({
  onPlaceSelected,
  restrictToViewBounds = false,
  countyBounds,
}) => {
  const map = useMap();
  const placesLibrary = useMapsLibrary('places');

  const [query, setQuery] = useState('');
  const [predictions, setPredictions] = useState<PlaceResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [markers, setMarkers] = useState<google.maps.Marker[]>([]);
  const autocompleteService =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesService = useRef<google.maps.places.PlacesService | null>(null);

  // Initialize Google Places services
  useEffect(() => {
    if (placesLibrary && map) {
      autocompleteService.current = new placesLibrary.AutocompleteService();
      placesService.current = new placesLibrary.PlacesService(map);
    }
  }, [placesLibrary, map]);

  // Clear markers when component unmounts
  useEffect(() => {
    return () => {
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [markers]);

  // Get current view bounds for search restrictions
  const getViewBounds = useCallback(() => {
    if (!map) return null;

    const bounds = map.getBounds();
    if (!bounds) return null;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    return {
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng(),
    };
  }, [map]);

  // Get custom county bounds or current view bounds
  const getSearchBounds = useCallback(() => {
    if (countyBounds) {
      return countyBounds;
    }
    return restrictToViewBounds ? getViewBounds() : null;
  }, [restrictToViewBounds, getViewBounds, countyBounds]);

  const searchPlaces = useCallback(
    async (searchQuery: string) => {
      if (
        !searchQuery.trim() ||
        !autocompleteService.current ||
        !placesLibrary
      ) {
        setPredictions([]);
        return;
      }

      setIsLoading(true);
      try {
        const bounds = getSearchBounds();
        const request: google.maps.places.AutocompletionRequest = {
          input: searchQuery,
          types: ['geocode'],
        };

        if (bounds) {
          const latLngBounds = new google.maps.LatLngBounds(
            { lat: bounds.south, lng: bounds.west },
            { lat: bounds.north, lng: bounds.east }
          );

          // Use locationBias to prefer results in the specified area
          request.locationRestriction = latLngBounds;

          // Use bounds for strict restriction (fallback for older API versions)
          request.bounds = latLngBounds;
        }

        autocompleteService.current.getPlacePredictions(
          request,
          (predictions, status) => {
            if (
              status === placesLibrary.PlacesServiceStatus.OK &&
              predictions
            ) {
              setPredictions(predictions as PlaceResult[]);
            } else {
              console.error('Places API error:', status);
              setPredictions([]);
            }
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error('Error fetching places:', error);
        setPredictions([]);
        setIsLoading(false);
      }
    },
    [placesLibrary, getSearchBounds]
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setQuery(value);
    setShowResults(true);
    setSelectedIndex(-1); // Reset selection when typing

    if (value.trim()) {
      searchPlaces(value);
    } else {
      setPredictions([]);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showResults || predictions.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev < predictions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : predictions.length - 1
        );
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < predictions.length) {
          handlePlaceSelect(predictions[selectedIndex].place_id);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handlePlaceSelect = (placeId: string) => {
    if (!placesService.current || !map || !placesLibrary) return;

    const request: google.maps.places.PlaceDetailsRequest = {
      placeId: placeId,
      fields: ['geometry', 'name', 'formatted_address', 'place_id'],
    };

    placesService.current.getDetails(request, (place, status) => {
      if (
        status === placesLibrary.PlacesServiceStatus.OK &&
        place &&
        place.geometry &&
        place.geometry.location
      ) {
        const { lat, lng } = place.geometry.location;

        // Clear existing markers
        markers.forEach((marker) => marker.setMap(null));
        setMarkers([]);

        // Create a custom marker with better styling
        const newMarker = new google.maps.Marker({
          position: { lat: lat(), lng: lng() },
          map: map,
          title: place.name || place.formatted_address,
          icon: {
            url:
              'data:image/svg+xml;charset=UTF-8,' +
              encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="16" fill="#1976d2" opacity="0.9"/>
                <circle cx="16" cy="16" r="8" fill="white"/>
                <circle cx="16" cy="16" r="4" fill="#1976d2"/>
              </svg>
            `),
            scaledSize: new google.maps.Size(32, 32),
            anchor: new google.maps.Point(16, 32),
          },
          zIndex: 1000,
        });

        setMarkers([newMarker]);

        // Pan to the location with smooth animation
        map.panTo({ lat: lat(), lng: lng() });
        map.setZoom(15);

        // Call callback if provided
        if (onPlaceSelected) {
          onPlaceSelected(place);
        }

        // Clear search
        setQuery('');
        setPredictions([]);
        setShowResults(false);
      } else {
        console.error('Error fetching place details:', status);
      }
    });
  };

  const clearSearch = () => {
    setQuery('');
    setPredictions([]);
    setShowResults(false);
    setSelectedIndex(-1);

    // Clear markers
    markers.forEach((marker) => marker.setMap(null));
    setMarkers([]);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minWidth: { xs: '150px', md: '280px' },//'300px',
        maxWidth: '100%',
        zIndex: 1000,
        padding: '0px 0px 0px 0px',
      }}
    >
      <Box
        className="google-places-search"
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: '10px',
          boxShadow: 2,
          padding: '0px 0px 0px 0px',
          width: '100%',
          minWidth: { xs: '150px', md: '280px' },
          height: '35px',
        }}
      >
        <SearchIcon
          sx={{ color: 'text.secondary', mr: 1, ml: 1, fontSize: '1.2rem' }}
        />
        <TextField
          fullWidth
          placeholder="Search for a location"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowResults(true)}
          variant="standard"
          size="small"
          slotProps={{
            input: {
              style: {
                fontSize: '0.875rem',
                padding: '4px 0',
                minHeight: 'unset',
              },
            },
          }}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiInput-underline:before': { borderBottom: 'none' },
            '& .MuiInput-underline:after': { borderBottom: 'none' },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottom: 'none',
            },
            '& .MuiInputBase-root': {
              minHeight: 'unset',
              padding: '0',
            },
            '& .MuiInputBase-input': {
              width: '100%',
            },
          }}
        />
        {isLoading && (
          <CircularProgress
            size={20}
            sx={{
              position: 'absolute',
              right: 40,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          />
        )}
        {query && !isLoading && (
          <IconButton
            size="small"
            onClick={clearSearch}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {showResults && predictions.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: 300,
            overflow: 'auto',
            boxShadow: 3,
            zIndex: 1001,
          }}
        >
          <List dense>
            {predictions.map((prediction, index) => (
              <ListItem
                key={prediction.place_id}
                onClick={() => handlePlaceSelect(prediction.place_id)}
                sx={{
                  cursor: 'pointer',
                  backgroundColor:
                    index === selectedIndex ? 'action.selected' : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      index === selectedIndex
                        ? 'action.selected'
                        : 'action.hover',
                  },
                }}
              >
                <ListItemText
                  primary={prediction.structured_formatting.main_text}
                  secondary={prediction.structured_formatting.secondary_text}
                  slotProps={{
                    primary: {
                      sx: {
                        fontSize: '0.875rem',
                        fontWeight: 500,
                      },
                    },
                    secondary: {
                      sx: {
                        fontSize: '0.75rem',
                      },
                    },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default GooglePlacesSearch;
